import React from "react";
import { apiRequest } from "../api/client";
import { useAuth } from "./AuthContext";
import { BillingMeResponse, CheckoutSessionResponse, PortalSessionResponse } from "../types/billing";

interface BillingContextValue {
  snapshot: BillingMeResponse | null;
  loading: boolean;
  refreshing: boolean;
  refreshBilling: () => Promise<void>;
  startCheckout: (interval: "month" | "year") => Promise<void>;
  openPortal: () => Promise<void>;
}

const BillingContext = React.createContext<BillingContextValue | undefined>(undefined);

export function BillingProvider({ children }: { children: React.ReactNode }) {
  const { token, user } = useAuth();
  const [snapshot, setSnapshot] = React.useState<BillingMeResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);

  const fetchBilling = React.useCallback(async () => {
    if (!token || !user) {
      setSnapshot(null);
      return;
    }
    const data = await apiRequest<BillingMeResponse>("/billing/me", { token });
    setSnapshot(data);
  }, [token, user]);

  const refreshBilling = React.useCallback(async () => {
    if (!token || !user) {
      setSnapshot(null);
      return;
    }
    setRefreshing(true);
    try {
      await fetchBilling();
    } finally {
      setRefreshing(false);
    }
  }, [token, user, fetchBilling]);

  React.useEffect(() => {
    let isMounted = true;
    if (!token || !user) {
      setSnapshot(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchBilling()
      .catch(() => {
        if (isMounted) setSnapshot(null);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [token, user, fetchBilling]);

  const startCheckout = React.useCallback(
    async (interval: "month" | "year") => {
      if (!token) return;
      const session = await apiRequest<CheckoutSessionResponse>("/billing/checkout-session", {
        method: "POST",
        token,
        body: { interval },
      });
      if (!session.url) throw new Error("No se pudo crear la sesión de checkout");
      window.location.assign(session.url);
    },
    [token]
  );

  const openPortal = React.useCallback(async () => {
    if (!token) return;
    const session = await apiRequest<PortalSessionResponse>("/billing/portal-session", {
      method: "POST",
      token,
    });
    if (!session.url) throw new Error("No se pudo abrir el portal de facturación");
    window.location.assign(session.url);
  }, [token]);

  const value = React.useMemo(
    () => ({
      snapshot,
      loading,
      refreshing,
      refreshBilling,
      startCheckout,
      openPortal,
    }),
    [snapshot, loading, refreshing, refreshBilling, startCheckout, openPortal]
  );

  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
}

export function useBilling() {
  const context = React.useContext(BillingContext);
  if (!context) {
    throw new Error("useBilling debe utilizarse dentro de BillingProvider");
  }
  return context;
}
